import React, { useState, useEffect, useCallback, useRef } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useSelector } from "react-redux";
import Header from "../components/header";
import "./calendar.css";
import NarrowDropdown2 from "../../components/input/new/narrowDropdown2";
import { DateTime } from "luxon";
import Spinner from "../../components/spinner";
import IconCheckbox from "../../components/input/new/iconCheckbox";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import AppointmentCard from "./appointmentCard";
import { authenticatedFetch } from "../../network/api";

// Set up localizer
const localizer = momentLocalizer(moment);

export default function MultiCalendarView() {
  const firmId = useSelector(
    (state) => state.attorneyData.attorneyData?.firm_id
  );

  const [professionals, setProfessionals] = useState([]);
  const [selectedLawyers, setSelectedLawyers] = useState([]);
  const [selectedLawyer, setSelectedLawyer] = useState(null); // Single lawyer for main view
  const [isLoading, setIsLoading] = useState(true);
  const [inviteError, setInviteError] = useState(null);
  // Add state for block day error
  const [blockDayError, setBlockDayError] = useState("");

  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState("week");
  const [miniCalendarDate, setMiniCalendarDate] = useState(new Date());

  const [eventsMap, setEventsMap] = useState({});

  // modal
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("blocker");
  const [modalStart, setModalStart] = useState(new Date());
  const [modalEnd, setModalEnd] = useState(new Date());
  const [currentLawyerId, setCurrentLawyerId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Recurrence form state
  const [freqType, setFreqType] = useState("weekly");
  const [interval, setInterval] = useState(1);
  const [selectedDays, setSelectedDays] = useState([]);
  const [monthlyMode, setMonthlyMode] = useState("onDate");
  const [monthlyDate, setMonthlyDate] = useState(moment(modalStart).date());
  const [monthlyNth, setMonthlyNth] = useState("First");
  const [monthlyWeekday, setMonthlyWeekday] = useState("Tue");

  const LOC_OPTIONS = [
    { label: "Office (Video Visit)", value: "video" },
    { label: "Office (In‑person Visit)", value: "inPerson" },
  ];

  const [location, setLocation] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const colors = [
    "#144A8C",
    "#2EB8BD",
    "#3881FE",
    "#4DA1E0",
    "#6BBFF5",
    "#95D1FF",
    "#F4A261",
    "#E76F51",
    "#2A9D8F",
    "#8338EC",
  ];

  // Tooltip state (kept for non-appointment events if needed)
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    content: "",
  });

  // Hover Appointment Card state
  const [hoverCard, setHoverCard] = useState({
    visible: false,
    position: { x: 0, y: 0 },
    event: null,
    anchorEl: null,
  });
  const hoverHideTimerRef = useRef(null);

  function computeCardPosition(anchorRect) {
    const CARD_WIDTH = 360;
    const CARD_HEIGHT = 280;
    const OFFSET = 12;

    // Horizontal positioning
    let x = anchorRect.right + OFFSET;
    if (x + CARD_WIDTH > window.innerWidth) {
      x = anchorRect.left - CARD_WIDTH - OFFSET;
    }

    // Vertical positioning - center by default
    let y = anchorRect.top + (anchorRect.height - CARD_HEIGHT) / 2;

    // If would clip at bottom, align card's bottom with slot's bottom
    if (y + CARD_HEIGHT > window.innerHeight) {
      y = anchorRect.bottom - CARD_HEIGHT;
    }
    // If would clip at top, align card's top with slot's top
    else if (y < 0) {
      y = anchorRect.top;
    }

    return { x, y };
  }

  const showHoverCard = (eventData, anchorEl) => {
    if (hoverHideTimerRef.current) {
      clearTimeout(hoverHideTimerRef.current);
      hoverHideTimerRef.current = null;
    }
    const rect = anchorEl.getBoundingClientRect();
    setHoverCard({
      visible: true,
      position: computeCardPosition(rect),
      event: eventData,
      anchorEl,
    });
  };

  const moveHoverCard = (anchorEl) => {
    const rect = anchorEl.getBoundingClientRect();
    setHoverCard((prev) =>
      prev.visible
        ? { ...prev, position: computeCardPosition(rect), anchorEl }
        : prev
    );
  };

  const scheduleHideHoverCard = () => {
    if (hoverHideTimerRef.current) clearTimeout(hoverHideTimerRef.current);
    hoverHideTimerRef.current = setTimeout(() => {
      setHoverCard({ visible: false, position: { x: 0, y: 0 }, event: null });
      hoverHideTimerRef.current = null;
    }, 200);
  };

  const cancelHideHoverCard = () => {
    if (hoverHideTimerRef.current) {
      clearTimeout(hoverHideTimerRef.current);
      hoverHideTimerRef.current = null;
    }
  };

  const [showAppointments, setShowAppointments] = useState(true);
  const [showAvailability, setShowAvailability] = useState(true);
  // Add state for locations (assuming only 'Office' for now)
  const [showOffice, setShowOffice] = useState(true);

  async function handleDelete() {
    if (!selectedEvent) return;
    setShowModal(false);

    const payload = {
      lawyer_id: currentLawyerId,
      start_ts: modalStart.toISOString(),
      end_ts: modalEnd.toISOString(),
    };

    await authenticatedFetch(`/api/attorney/webapp/calendar/busyBlock`, {
      method: "DELETE",
      body: JSON.stringify(payload),
    });

    const updated = await fetchEventsForLawyer(currentLawyerId);
    setEventsMap((prev) => ({
      ...prev,
      [currentLawyerId]: updated,
    }));

    setSelectedEvent(null);
  }



  const fetchEventsForLawyer = useCallback(
    async (lawyerId) => {
      const from = moment(currentDate).startOf("week").toISOString();
      const to = moment(currentDate).endOf("week").toISOString();
      
      
      try {
        const res = await authenticatedFetch(
          `/api/attorney/webapp/calendar/fetchCalendarEvents?lawyerId=${lawyerId}&from=${from}&to=${to}`
        );
        
        console.log(`[fetchEventsForLawyer] Response status:`, res.status);
        
        if (!res.ok) {
          const errorText = await res.text();
          console.error(`[fetchEventsForLawyer] API error:`, errorText);
          throw new Error(`Failed to load events: ${res.status} ${errorText}`);
        }
        
        const data = await res.json();
        console.log(`[fetchEventsForLawyer] Raw API response:`, data);
        
        if (!data.events || !Array.isArray(data.events)) {
          console.error(`[fetchEventsForLawyer] Invalid events data:`, data);
          return [];
        }
        
        const mappedEvents = data.events.map((e) => {
          console.log(`[fetchEventsForLawyer] Processing event:`, e);
          
          const startUTC = DateTime.fromISO(e.start, { zone: "utc" });
          const endUTC = DateTime.fromISO(e.end, { zone: "utc" });
          
          const mappedEvent = {
            ...e,
            start: startUTC.setZone("local").toJSDate(),
            end: endUTC.setZone("local").toJSDate(),
          };
          
          console.log(`[fetchEventsForLawyer] Mapped event:`, mappedEvent);
          return mappedEvent;
        });
        
        console.log(`[fetchEventsForLawyer] Final mapped events for lawyer ${lawyerId}:`, mappedEvents);
        return mappedEvents;
        
      } catch (error) {
        console.error(`[fetchEventsForLawyer] Error for lawyer ${lawyerId}:`, error);
        throw error;
      }
    },
    [currentDate]
  );

  // const fetchEventsForLawyer = useCallback(
  //   async (lawyerId) => {
  //     // Get the week range in the user's local timezone
  //     const from = moment(currentDate).startOf("week").toISOString();
  //     const to = moment(currentDate).endOf("week").toISOString();
      
  //     console.log(`[fetchEventsForLawyer] Fetching events for lawyer ${lawyerId} from ${from} to ${to}`);
      
  //     try {
  //       const res = await authenticatedFetch(
  //         `/api/attorney/webapp/calendar/fetchCalendarEvents?lawyerId=${lawyerId}&from=${from}&to=${to}`
  //       );
        
  //       console.log(`[fetchEventsForLawyer] Response status:`, res.status);
        
  //       if (!res.ok) {
  //         const errorText = await res.text();
  //         console.error(`[fetchEventsForLawyer] API error:`, errorText);
  //         throw new Error(`Failed to load events: ${res.status} ${errorText}`);
  //       }
        
  //       const data = await res.json();
  //       console.log(`[fetchEventsForLawyer] Raw API response:`, data);
        
  //       if (!data.events || !Array.isArray(data.events)) {
  //         console.error(`[fetchEventsForLawyer] Invalid events data:`, data);
  //         return [];
  //       }
        
  //       const mappedEvents = data.events.map((e) => {
  //         console.log(`[fetchEventsForLawyer] Processing event:`, e);
          
  //         // Parse the UTC timestamp from the database
  //         const startUTC = DateTime.fromISO(e.start, { zone: "utc" });
  //         const endUTC = DateTime.fromISO(e.end, { zone: "utc" });
          
  //         // Convert to Asia/Shanghai to get the original scheduled time
  //         const startShanghai = startUTC.setZone("Asia/Shanghai");
  //         const endShanghai = endUTC.setZone("Asia/Shanghai");
          
  //         // Now convert that same wall-clock time to the user's local timezone
  //         // This preserves the original 8am-5pm scheduling but shows it in local time
  //         const startLocal = startShanghai.setZone("local", { keepLocalTime: true });
  //         const endLocal = endShanghai.setZone("local", { keepLocalTime: true });
          
  //         const mappedEvent = {
  //           ...e,
  //           start: startLocal.toJSDate(),
  //           end: endLocal.toJSDate(),
  //         };
          
  //         console.log(`[fetchEventsForLawyer] Mapped event:`, mappedEvent);
  //         return mappedEvent;
  //       });
        
  //       console.log(`[fetchEventsForLawyer] Final mapped events for lawyer ${lawyerId}:`, mappedEvents);
  //       return mappedEvents;
        
  //     } catch (error) {
  //       console.error(`[fetchEventsForLawyer] Error for lawyer ${lawyerId}:`, error);
  //       throw error;
  //     }
  //   },
  //   [currentDate]
  // );



  useEffect(() => {
    if (!hoverCard.visible || !hoverCard.anchorEl) return;

    const handleRelayout = () => {
      const rect = hoverCard.anchorEl.getBoundingClientRect();
      setHoverCard((h) => ({ ...h, position: computeCardPosition(rect) }));
    };

    // capture-phase scroll so we catch scrolls on inner containers
    window.addEventListener("scroll", handleRelayout, {
      passive: true,
      capture: true,
    });
    window.addEventListener("resize", handleRelayout);

    return () => {
      window.removeEventListener("scroll", handleRelayout, true);
      window.removeEventListener("resize", handleRelayout);
    };
  }, [hoverCard.visible, hoverCard.anchorEl]);

  // load professionals
  useEffect(() => {
    async function loadProfessionals() {
      try {
        const res = await fetch(
          `/api/attorney/fetchFirmMembers?firm_id=${firmId}`
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Fetch failed");
        setProfessionals(data.lawyers);
        setSelectedLawyers(data.lawyers.map((l) => l.user_id));
        // Set first lawyer as selected by default
        if (data.lawyers.length > 0) {
          setSelectedLawyer(data.lawyers[0].user_id);
        }
      } catch (err) {
        console.error(err);
        setInviteError("Failed to load professionals.");
      } finally {
        setIsLoading(false);
      }
    }
    if (firmId) loadProfessionals();
  }, [firmId]);

  // load events
  useEffect(() => {
    if (!selectedLawyers.length) return;
    
    (async () => {
      const map = {};
      await Promise.all(
        selectedLawyers.map(async (lawyerId) => {
          try {
            map[lawyerId] = await fetchEventsForLawyer(lawyerId);
          } catch (error) {
            map[lawyerId] = [];
          }
        })
      );
      // Ensure all selectedLawyers have an entry, even if empty
      selectedLawyers.forEach((lawyerId) => {
        if (!map[lawyerId]) map[lawyerId] = [];
      });
      
      setEventsMap(map);
    })();
  }, [selectedLawyers, currentDate, fetchEventsForLawyer]);

  // Update selected lawyer when selectedLawyers changes
  useEffect(() => {
    if (
      selectedLawyers.length > 0 &&
      !selectedLawyers.includes(selectedLawyer)
    ) {
      setSelectedLawyer(selectedLawyers[0]);
    }
  }, [selectedLawyers, selectedLawyer]);

  // modal openers
  const openBlockerModal = (event) => {
    console.log("[openBlockerModal] event:", event);
    console.log("[openBlockerModal] event.eventType:", event.eventType);
    console.log("[openBlockerModal] event.resource:", event.resource);
    console.log("[openBlockerModal] event.resource?.lawyerId:", event.resource?.lawyerId);
    
    if (event.eventType === "appointment") {
      console.log("[openBlockerModal] Not opening for appointment event");
      return;
    }
    
    // Safety check for lawyerId
    const lawyerId = event.resource?.lawyerId;
    if (!lawyerId) {
      console.error("[openBlockerModal] Missing lawyerId for event:", event);
      return;
    }
    
    console.log("[openBlockerModal] Opening blocker modal for lawyer:", lawyerId);
    setModalType("blocker");
    setModalStart(event.start);
    setModalEnd(event.end);
    setCurrentLawyerId(lawyerId);
    setSelectedEvent(event);
    setShowModal(true);
  };

  const openAvailabilityModal = (slotInfo) => {
    console.log("[openAvailabilityModal] called for", slotInfo);
    
    // Safety check for lawyerId
    const lawyerId = slotInfo.resource?.lawyerId;
    if (!lawyerId) {
      console.error("[openAvailabilityModal] Missing lawyerId for slotInfo:", slotInfo);
      return;
    }
    
    setModalType("availability");
    setModalStart(slotInfo.start);
    setModalEnd(slotInfo.end);
    setCurrentLawyerId(lawyerId);
    setShowModal(true);
  };

  const openBlockDayModal = (date, lawyerId) => {
    console.log("[openBlockDayModal] called for", date, "lawyerId:", lawyerId);
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setDate(dayEnd.getDate());
    dayEnd.setHours(23, 59, 0, 0);

    // Ensure eventsMap is always an array
    const events = eventsMap[lawyerId] || [];
    const hasAppointment = events.some(
      (e) =>
        e.eventType === "appointment" &&
        ((e.start >= dayStart && e.start <= dayEnd) ||
          (e.end >= dayStart && e.end <= dayEnd) ||
          (e.start <= dayStart && e.end >= dayEnd))
    );
    if (hasAppointment) {
      console.log("[openBlockDayModal] Block prevented due to appointment");
      setBlockDayError(
        "Cannot block this day: there is an appointment scheduled."
      );
      setTimeout(() => setBlockDayError(""), 3000);
      return;
    }
    console.log("[openBlockDayModal] Opening block day modal");
    setModalType("blockDay");
    setModalStart(dayStart);
    setModalEnd(dayEnd);
    setCurrentLawyerId(lawyerId);
    setShowModal(true);
  };

  // Helper to check if a slot overlaps with any event type
  function getSlotOverlapType(slotStart, slotEnd, events) {
    let hasAppointment = false;
    let hasAvailability = false;
    events.forEach((e) => {
      if (!e.start || !e.end) return;
      const s1 = slotStart.getTime();
      const e1 = slotEnd.getTime();
      const s2 = e.start.getTime();
      const e2 = e.end.getTime();
      const overlap = s1 < e2 && e1 > s2;
      if (overlap) {
        if (e.eventType === "appointment") hasAppointment = true;
        if (e.eventType === "availability") hasAvailability = true;
      }
    });
    if (hasAppointment) return "appointment";
    if (hasAvailability) return "availability";
    return null;
  }

  async function handleModalSubmit() {
    setShowModal(false);

    if (modalType === "availability") {
      const WEEKDAY_IDX = { MO: 0, TU: 1, WE: 2, TH: 3, FR: 4, SA: 5, SU: 6 };
      const todayIdx = (modalStart.getDay() + 6) % 7;
      const defaultCode =
        Object.entries(WEEKDAY_IDX).find(([, idx]) => idx === todayIdx)?.[0] ||
        Object.keys(WEEKDAY_IDX)[0];
      const days = selectedDays.length ? selectedDays : [defaultCode];

      const slots = days.map((code) => {
        const base = {
          day_of_week: WEEKDAY_IDX[code],
          start_time: moment(modalStart).utc().format("HH:mm:ss"),
          end_time: moment(modalEnd).utc().format("HH:mm:ss"),
          recurrence_type: freqType,
          interval,
          location_types: location ? [location] : [], // Ensure array
          start_date: null,
          end_end: null,
        };

        if (freqType === "monthly") {
          if (monthlyMode === "onDate") {
            base.start_date = moment(modalStart).format("YYYY-MM-DD");
            base.rrule = `FREQ=MONTHLY;INTERVAL=${interval};BYMONTHDAY=${monthlyDate}`;
          } else {
            const Nstr = { First: 1, Second: 2, Third: 3, Fourth: 4, Last: -1 }[
              monthlyNth
            ];
            const WD = monthlyWeekday.slice(0, 2).toUpperCase();
            base.rrule = `FREQ=MONTHLY;INTERVAL=${interval};BYDAY=${Nstr}${WD}`;
          }
        } else {
          base.rrule = `FREQ=WEEKLY;INTERVAL=${interval};BYDAY=${code}`;
        }

        return base;
      });

      await authenticatedFetch(
        `/api/attorney/webapp/calendar/recurringSchedules?user_id=${currentLawyerId}`,
        {
          method: "POST",
          body: JSON.stringify({ slots, initial: false }),
        }
      );
    } else {
      await authenticatedFetch("/api/attorney/webapp/calendar/busyBlock", {
        method: "POST",
        body: JSON.stringify({
          lawyer_id: currentLawyerId,
          start_ts: modalStart.toISOString(),
          end_ts: modalEnd.toISOString(),
        }),
      });
    }

    const updated = await fetchEventsForLawyer(currentLawyerId);
    setEventsMap((prev) => ({
      ...prev,
      [currentLawyerId]: updated,
    }));
  }

  // navigation
  const handleNavigate = (action) => {
    let newDate = currentDate;
    if (action === "prev")
      newDate = moment(currentDate).subtract(1, "week").toDate();
    if (action === "next")
      newDate = moment(currentDate).add(1, "week").toDate();
    if (action === "TODAY") newDate = new Date();
    setCurrentDate(newDate);
  };

  const handleView = (view) => setCurrentView(view);

  // Mini calendar functions
  const handleMiniCalendarNavigate = (direction) => {
    if (direction === "prev") {
      setMiniCalendarDate(
        moment(miniCalendarDate).subtract(1, "month").toDate()
      );
    } else if (direction === "next") {
      setMiniCalendarDate(moment(miniCalendarDate).add(1, "month").toDate());
    }
  };

  const handleMiniDateClick = (date) => {
    setCurrentDate(date);
  };

  const generateMiniCalendarDays = () => {
    const startOfMonth = moment(miniCalendarDate).startOf("month");
    const endOfMonth = moment(miniCalendarDate).endOf("month");
    const startOfCalendar = moment(startOfMonth).startOf("week");
    const endOfCalendar = moment(endOfMonth).endOf("week");

    const days = [];
    let current = moment(startOfCalendar);

    while (current.isSameOrBefore(endOfCalendar)) {
      days.push({
        date: current.toDate(),
        isCurrentMonth: current.isSame(miniCalendarDate, "month"),
        isToday: current.isSame(moment(), "day"),
        isSelected: current.isSame(currentDate, "day"),
      });
      current.add(1, "day");
    }

    return days;
  };

  const eventStyleGetter = (event, start, end, isSelected, lawyerId) => {
    const idx = professionals.findIndex((p) => p.user_id === lawyerId);
    const color = colors[idx % colors.length];

    const isFullDayBlocker =
      event.eventType === "blocker" &&
      start.getHours() === 0 &&
      start.getMinutes() === 0 &&
      end.getHours() === 23 &&
      end.getMinutes() === 59;

    let backgroundColor;
    if (event.eventType === "appointment") {
      backgroundColor = color;
    } else if (event.eventType === "blocker") {
      backgroundColor = "#e0e0e0";
    } else {
      backgroundColor = "#fff";
    }

    let borderLeft;
    let borderRight;
    let borderTop;
    let borderBottom;
    if (event.eventType === "appointment") {
      borderLeft = `1px solid ${color}`;
      borderRight = `1px solid ${color}`;
      borderBottom = `1px solid ${color}`;
      borderTop = `1px solid ${color}`;
    } else if (event.eventType === "blocker") {
      borderLeft = `1px solid #E0E0E0`;
      borderRight = `1px solid #E0E0E0`;
      borderBottom = `1px solid #E0E0E0`;
      borderTop = `1px solid #E0E0E0`;
    } else {
      borderLeft = `14px solid ${color}`;
      borderRight = `1px solid ${color}`;
      borderBottom = `1px solid ${color}`;
      borderTop = `1px solid ${color}`;
    }

    var base = {
      backgroundColor,
      color: event.eventType === "appointment" ? "#fff" : "#333",
      borderLeft,
      borderTop,
      borderRight,
      borderBottom,
      borderRadius: 0,
      fontSize: 12,
      padding: "2px 5px",
    };

    if (isFullDayBlocker) {
      base = {
        backgroundColor,
        color: "#333",
        border: "none !important",
        borderRadius: "0px !important;",
        padding: "2px 5px",
        width: "100% !important",
        left: "0 !important",
      };
    }

    return { style: base, className: `${event.eventType}-event` };
  };

  // Get availability events for selected lawyer
  const getAvailabilityEvents = (lawyerId) => {
    const allEvents = eventsMap[lawyerId] || [];
    const availEvents = allEvents.filter((e) => e.eventType === "availability");
    return availEvents.map((e) => ({
      start: e.start,
      end: e.end,
      resource: { lawyerId },
      eventType: "availability",
      allDay: false,
      background: true,
    }));
  };

  const EventComponent = ({ event, allEvents }) => {
    let containerStyle = {};
    if (event.eventType === "availability" && allEvents) {
      const s1 = event.start.getTime();
      const e1 = event.end.getTime();
      const overlap = allEvents.some(
        (e) =>
          (e.eventType === "appointment" || e.eventType === "blocker") &&
          e !== event &&
          s1 < e.end.getTime() &&
          e1 > e.start.getTime()
      );
      if (overlap) {
        containerStyle = { marginTop: "32px" };
      }
    }

    const handleMouseEnter = (e) => {
      if (event.eventType === "appointment") {
        showHoverCard(event, e.currentTarget);
      }
    };
    const handleMouseLeave = () => {
      if (event.eventType === "appointment") {
        scheduleHideHoverCard();
      } else {
        setTooltip((t) => ({ ...t, visible: false }));
      }
    };

    return (
      <div
        onMouseEnter={handleMouseEnter}
        onMouseMove={(e) => {
          if (event.eventType === "appointment") {
            moveHoverCard(e.currentTarget);
          }
        }}
        onMouseLeave={handleMouseLeave}
        style={containerStyle}
      >
        {event.eventType === "availability" && (
          <span className="event-time">
            {moment(event.start).format("h:mm A")} –{" "}
            {moment(event.end).format("h:mm A")}
          </span>
        )}
        <span className="event-title">{event.title}</span>
      </div>
    );
  };

  const makeSlotWrapper =
    (lawyerId) =>
    ({ value, children }) => (
      <div
        className="rbc-time-slot-wrapper"
        onClick={() => openBlockDayModal(value, lawyerId)}
        style={{ height: "100%" }}
      >
        {children}
      </div>
    );

  const makeDateHeader =
    (lawyerId) =>
    ({ date, label }) => {
      console.log(
        "[makeDateHeader] Clickable header rendered for",
        date,
        "lawyerId:",
        lawyerId
      );
      return (
        <div
          className="rbc-time-header-content"
          onClick={() => {
            console.log(
              "[makeDateHeader] Clicked header for",
              date,
              "lawyerId:",
              lawyerId
            );
            openBlockDayModal(date, lawyerId);
          }}
        >
          {label}
        </div>
      );
    };

  // Lawyer Tabs Component for Week View
  const LawyerTabs = () => {
    if (currentView !== "week") return null;

    return (
      <div className="lawyer-tabs">
        {selectedLawyers.map((lawyerId) => {
          const lawyer = professionals.find((p) => p.user_id === lawyerId);
          const idx = professionals.findIndex((p) => p.user_id === lawyerId);
          const color = colors[idx % colors.length];
          const isActive = selectedLawyer === lawyerId;

          return (
            <button
              key={lawyerId}
              className={`lawyer-tab ${isActive ? "active" : ""}`}
              onClick={() => setSelectedLawyer(lawyerId)}
            >
              <div
                className="tab-color-indicator"
                style={{ backgroundColor: color }}
              />
              <span>{lawyer?.name}</span>
            </button>
          );
        })}
      </div>
    );
  };

  // Mini Calendar Component
  const MiniCalendar = () => {
    const days = generateMiniCalendarDays();
    const dayHeaders = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

    return (
      <div className="mini-calendar">
        <div className="mini-calendar-header">
          <button
            className="mini-nav-btn"
            onClick={() => handleMiniCalendarNavigate("prev")}
          >
            ‹
          </button>
          <span className="mini-calendar-title">
            {moment(miniCalendarDate).format("MMMM YYYY")}
          </span>
          <button
            className="mini-nav-btn"
            onClick={() => handleMiniCalendarNavigate("next")}
          >
            ›
          </button>
        </div>

        <div className="mini-calendar-grid">
          <div className="mini-calendar-days">
            {dayHeaders.map((day) => (
              <div key={day} className="mini-day-header">
                {day}
              </div>
            ))}
          </div>

          <div className="mini-calendar-dates">
            {days.map((day, index) => (
              <div
                key={index}
                className={`mini-date ${
                  !day.isCurrentMonth ? "other-month" : ""
                } ${day.isToday ? "today" : ""} ${day.isSelected ? "selected" : ""}`}
                onClick={() => handleMiniDateClick(day.date)}
              >
                {moment(day.date).format("D")}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Get formatted date range for toolbar
  const getDateRange = () => {
    if (currentView === "day") {
      return moment(currentDate).format("MMMM D, YYYY");
    } else if (currentView === "week") {
      const start = moment(currentDate).startOf("week");
      const end = moment(currentDate).endOf("week");
      return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
    } else {
      return moment(currentDate).format("MMMM YYYY");
    }
  };

  // Render Calendar Content
  const renderCalendarContent = () => {
    if (currentView === "day") {
      // Multiple Calendar components side by side for each lawyer
      return (
        <div className="multi-calendar-day-view">
          {selectedLawyers.map((lawyerId) => {
            const lawyer = professionals.find((p) => p.user_id === lawyerId);
            const allEvents = eventsMap[lawyerId] || [];

            return (
              <div key={lawyerId} className="lawyer-calendar-container">
                <div className="lawyer-calendar-header">
                  <div
                    className="lawyer-color-indicator"
                    style={{
                      backgroundColor:
                        colors[
                          professionals.findIndex(
                            (p) => p.user_id === lawyerId
                          ) % colors.length
                        ],
                    }}
                  />
                  <span>{lawyer?.name}</span>
                </div>
                <Calendar
                  localizer={localizer}
                  events={allEvents}
                  startAccessor="start"
                  endAccessor="end"
                  view="day"
                  views={["day"]}
                  date={currentDate}
                  onNavigate={setCurrentDate}
                  onView={handleView}
                  eventPropGetter={(event) =>
                    eventStyleGetter(
                      event,
                      event.start,
                      event.end,
                      false,
                      lawyerId
                    )
                  }
                  components={{
                    event: ({ event }) => (
                      <EventComponent event={event} allEvents={allEvents} />
                    ),
                    dateHeader: makeDateHeader(lawyerId),
                  }}
                  onSelectEvent={(event) => {
                    console.log("[Calendar] onSelectEvent", event);
                    openBlockerModal(event);
                  }}
                  onSelectSlot={(slotInfo) => {
                    console.log("[Calendar] onSelectSlot", slotInfo);
                    const isAllDay =
                      slotInfo.start.getHours() === 0 &&
                      slotInfo.start.getMinutes() === 0 &&
                      slotInfo.end.getHours() === 0 &&
                      slotInfo.end.getMinutes() === 0 &&
                      (slotInfo.end - slotInfo.start) /
                        (1000 * 60 * 60 * 24) ===
                        1;
                    const events = allEvents || [];
                    const overlapType = getSlotOverlapType(
                      slotInfo.start,
                      slotInfo.end,
                      events
                    );
                    if (isAllDay) {
                      const dayStart = new Date(slotInfo.start);
                      dayStart.setHours(0, 0, 0, 0);
                      const dayEnd = new Date(slotInfo.end);
                      dayEnd.setHours(0, 0, 0, 0);
                      const hasAppointment = events.some(
                        (e) =>
                          e.eventType === "appointment" &&
                          ((e.start >= dayStart && e.start < dayEnd) ||
                            (e.end > dayStart && e.end <= dayEnd) ||
                            (e.start <= dayStart && e.end >= dayEnd))
                      );
                      if (hasAppointment) {
                        setBlockDayError(
                          "Cannot block this day: there is an appointment scheduled."
                        );
                        setTimeout(() => setBlockDayError(""), 3000);
                        return;
                      }
                      openBlockDayModal(slotInfo.start, lawyerId);
                      return;
                    }
                    if (overlapType === "appointment") {
                      console.log(
                        "[Calendar] onSelectSlot: overlapType is appointment, returning"
                      );
                      return;
                    } else if (overlapType === "availability") {
                      console.log(
                        "[Calendar] onSelectSlot: overlapType is availability, opening blocker modal"
                      );
                      openBlockerModal({
                        start: slotInfo.start,
                        end: slotInfo.end,
                        resource: { lawyerId },
                        eventType: "blocker",
                      });
                      return;
                    }

                    console.log(
                      "[Calendar] onSelectSlot: opening availability modal"
                    );
                    openAvailabilityModal({
                      start: slotInfo.start,
                      end: slotInfo.end,
                      resource: { lawyerId },
                    });
                  }}
                  selectable
                  step={15}
                  timeslots={4}
                  min={new Date(2024, 0, 1, 8, 0)}
                  max={new Date(2024, 0, 1, 21, 0)}
                />
              </div>
            );
          })}
        </div>
      );
    } else if (currentView === "week") {
      // Single lawyer view for week
      // const events = eventsMap[selectedLawyer] || [];
      // const availabilityEvents = getAvailabilityEvents(selectedLawyer);
      // const allEvents = [...events, ...availabilityEvents];

      const allEvents = eventsMap[selectedLawyer] || [];

      return (
        <div className="single-calendar-view">
          <div className="selected-lawyer-header">
            <div
              className="lawyer-color-indicator"
              style={{
                backgroundColor:
                  colors[
                    professionals.findIndex(
                      (p) => p.user_id === selectedLawyer
                    ) % colors.length
                  ],
              }}
            />
            <span>
              {professionals.find((p) => p.user_id === selectedLawyer)?.name}
            </span>
          </div>
          <Calendar
            localizer={localizer}
            events={allEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: "100%" }}
            view={currentView}
            views={["week"]}
            date={currentDate}
            onNavigate={setCurrentDate}
            onView={handleView}
            eventPropGetter={(event) =>
              eventStyleGetter(
                event,
                event.start,
                event.end,
                false,
                selectedLawyer
              )
            }
            components={{
              event: ({ event }) => (
                <EventComponent event={event} allEvents={allEvents} />
              ),
              dateHeader: makeDateHeader(selectedLawyer),
            }}
            onSelectEvent={(event) => {
              console.log("[Calendar] onSelectEvent", event);
              openBlockerModal(event);
            }}
            onSelectSlot={(slotInfo) => {
              console.log("[Calendar] onSelectSlot", slotInfo);
              // Detect all-day slot (date header click)
              const isAllDay =
                slotInfo.start.getHours() === 0 &&
                slotInfo.start.getMinutes() === 0 &&
                slotInfo.end.getHours() === 0 &&
                slotInfo.end.getMinutes() === 0 &&
                (slotInfo.end - slotInfo.start) / (1000 * 60 * 60 * 24) === 1;
              const events = allEvents || [];
              const overlapType = getSlotOverlapType(
                slotInfo.start,
                slotInfo.end,
                events
              );
              if (isAllDay) {
                const dayStart = new Date(slotInfo.start);
                dayStart.setHours(0, 0, 0, 0);
                const dayEnd = new Date(slotInfo.end);
                dayEnd.setHours(0, 0, 0, 0);
                const hasAppointment = events.some(
                  (e) =>
                    e.eventType === "appointment" &&
                    ((e.start >= dayStart && e.start < dayEnd) ||
                      (e.end > dayStart && e.end <= dayEnd) ||
                      (e.start <= dayStart && e.end >= dayEnd))
                );
                if (hasAppointment) {
                  setBlockDayError(
                    "Cannot block this day: there is an appointment scheduled."
                  );
                  setTimeout(() => setBlockDayError(""), 3000);
                  return;
                }
                openBlockDayModal(slotInfo.start, selectedLawyer);
                return;
              }
              if (overlapType === "appointment") {
                console.log(
                  "[Calendar] onSelectSlot: overlapType is appointment, returning"
                );
                return;
              } else if (overlapType === "availability") {
                console.log(
                  "[Calendar] onSelectSlot: overlapType is availability, opening blocker modal"
                );
                openBlockerModal({
                  start: slotInfo.start,
                  end: slotInfo.end,
                  resource: { lawyerId: selectedLawyer },
                  eventType: "blocker",
                });
                return;
              }

              console.log(
                "[Calendar] onSelectSlot: opening availability modal"
              );
              openAvailabilityModal({
                start: slotInfo.start,
                end: slotInfo.end,
                resource: { lawyerId: selectedLawyer },
              });
            }}
            selectable
            step={15}
            timeslots={4}
            min={new Date(2024, 0, 1, 8, 0)}
            max={new Date(2024, 0, 1, 21, 0)}
          />
        </div>
      );
    }

    return null;
  };

  if (isLoading) {
    return (
      <div className="calendar-container">
        <Header />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-container">
      <Header />
      {inviteError && <p className="error-message">{inviteError}</p>}

      <div className="calendar-content">
        {/* Sidebar */}
        <div className="calendar-sidebar">
          <MiniCalendar />

          <div className="timezone-section">
            <h3>Time zone</h3>
            <select className="timezone-select" defaultValue="central">
              <option value="central">Central (Chicago)</option>
              <option value="eastern">Eastern (New York)</option>
              <option value="pacific">Pacific (Los Angeles)</option>
            </select>
          </div>

          <div className="calendar-filters">
            <h3>Display on my calendars</h3>
            <div className="filter-group">
              <IconCheckbox
                label="Appointments"
                checked={showAppointments}
                onChange={setShowAppointments}
                className="calendar-filter-checkbox"
              />
              <IconCheckbox
                label="Availability"
                checked={showAvailability}
                onChange={setShowAvailability}
                className="calendar-filter-checkbox"
              />
            </div>
            <div className="filter-group">
              <h4>Providers</h4>
              {professionals.map((lawyer, idx) => (
                <div
                  key={lawyer.user_id}
                  className="filter-option"
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <IconCheckbox
                    label={lawyer.name}
                    checked={selectedLawyers.includes(lawyer.user_id)}
                    onChange={(checked) => {
                      if (checked) {
                        setSelectedLawyers((prev) => [...prev, lawyer.user_id]);
                      } else {
                        setSelectedLawyers((prev) =>
                          prev.filter((id) => id !== lawyer.user_id)
                        );
                      }
                    }}
                    className="calendar-filter-checkbox"
                  />
                  <div
                    className="color-indicator"
                    style={{
                      backgroundColor: colors[idx % colors.length],
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="filter-group">
              <h4>Locations</h4>
              <div
                className="filter-option"
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <IconCheckbox
                  label="Office"
                  checked={showOffice}
                  onChange={setShowOffice}
                  className="calendar-filter-checkbox"
                />
                <div
                  className="color-indicator"
                  style={{
                    backgroundColor: "#28a745",
                    width: 16,
                    height: 16,
                    borderRadius: 4,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Calendar */}
        <div className="calendar-main">
          {/* Show block day error if present */}
          {blockDayError && (
            <div
              className="error-message"
              style={{ color: "red", marginBottom: 8 }}
            >
              {blockDayError}
            </div>
          )}
          <div className="main-calendar-toolbar">
            <div className="toolbar-left">
              <div className="date-range">{getDateRange()}</div>
              <button
                className="today-btn"
                onClick={() => handleNavigate("TODAY")}
              >
                Today
              </button>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="nav-btn"
                  onClick={() => handleNavigate("prev")}
                >
                  <ChevronLeft />
                </button>
                <button
                  className="nav-btn"
                  onClick={() => handleNavigate("next")}
                >
                  <ChevronRight />
                </button>
              </div>
            </div>

            <div className="view-controls">
              <div className="view-toggle-group">
                <button
                  className={`view-btn ${currentView === "day" ? "active" : ""}`}
                  onClick={() => handleView("day")}
                >
                  Day
                </button>
                <button
                  className={`view-btn ${currentView === "week" ? "active" : ""}`}
                  onClick={() => handleView("week")}
                >
                  Week
                </button>
              </div>
            </div>
          </div>

          {/* Lawyer Tabs (only shows in week view) */}
          <LawyerTabs />

          {/* Main Calendar Container */}
          <div className="main-calendar-container">
            {renderCalendarContent()}
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div
            className="modal-content-calendar"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                {modalType === "availability"
                  ? "Set Availability"
                  : modalType === "blockDay"
                    ? "Block Day"
                    : "Edit Block"}
              </h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <div className="modal-divider" />

            <div className="modal-body">
              {modalType === "availability" ? (
                <div className="recurring-modal-body">
                  <div className="recurring-left">
                    <div className="form-row">
                      <label>Start:</label>
                      <input
                        type="time"
                        value={moment(modalStart).format("HH:mm")}
                        onChange={(e) => {
                          const [hours, minutes] = e.target.value.split(":");
                          const newStart = new Date(modalStart);
                          newStart.setHours(parseInt(hours), parseInt(minutes));
                          setModalStart(newStart);
                        }}
                      />
                    </div>

                    <div className="form-row">
                      <label>End:</label>
                      <input
                        type="time"
                        value={moment(modalEnd).format("HH:mm")}
                        onChange={(e) => {
                          const [hours, minutes] = e.target.value.split(":");
                          const newEnd = new Date(modalEnd);
                          newEnd.setHours(parseInt(hours), parseInt(minutes));
                          setModalEnd(newEnd);
                        }}
                      />
                    </div>

                    <div className="form-row">
                      <label>Repeat:</label>
                      <select
                        value={freqType}
                        onChange={(e) => setFreqType(e.target.value)}
                      >
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    <div className="form-row">
                      <label>Every:</label>
                      <input
                        type="number"
                        min="1"
                        value={interval}
                        onChange={(e) => setInterval(parseInt(e.target.value))}
                      />
                      <span>
                        {freqType === "weekly" ? "week(s)" : "month(s)"}
                      </span>
                    </div>

                    {freqType === "weekly" && (
                      <div className="form-row">
                        <label>Days:</label>
                        <div className="weekdays-row">
                          {["MO", "TU", "WE", "TH", "FR", "SA", "SU"].map(
                            (day) => (
                              <button
                                key={day}
                                type="button"
                                className={`day-btn ${selectedDays.includes(day) ? "active" : ""}`}
                                onClick={() => {
                                  setSelectedDays((prev) =>
                                    prev.includes(day)
                                      ? prev.filter((d) => d !== day)
                                      : [...prev, day]
                                  );
                                }}
                              >
                                {day}
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    )}

                    {freqType === "monthly" && (
                      <>
                        <div className="form-row">
                          <input
                            type="radio"
                            id="onDate"
                            name="monthlyMode"
                            value="onDate"
                            checked={monthlyMode === "onDate"}
                            onChange={(e) => setMonthlyMode(e.target.value)}
                          />
                          <label htmlFor="onDate">On date {monthlyDate}</label>
                        </div>
                        <div className="form-row">
                          <input
                            type="radio"
                            id="onWeekday"
                            name="monthlyMode"
                            value="onWeekday"
                            checked={monthlyMode === "onWeekday"}
                            onChange={(e) => setMonthlyMode(e.target.value)}
                          />
                          <label htmlFor="onWeekday">On</label>
                          <select
                            value={monthlyNth}
                            onChange={(e) => setMonthlyNth(e.target.value)}
                          >
                            <option value="First">First</option>
                            <option value="Second">Second</option>
                            <option value="Third">Third</option>
                            <option value="Fourth">Fourth</option>
                            <option value="Last">Last</option>
                          </select>
                          <select
                            value={monthlyWeekday}
                            onChange={(e) => setMonthlyWeekday(e.target.value)}
                          >
                            <option value="Monday">Monday</option>
                            <option value="Tuesday">Tuesday</option>
                            <option value="Wednesday">Wednesday</option>
                            <option value="Thursday">Thursday</option>
                            <option value="Friday">Friday</option>
                            <option value="Saturday">Saturday</option>
                            <option value="Sunday">Sunday</option>
                          </select>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="recurring-divider" />

                  <div className="recurring-right">
                    <div className="form-row">
                      <label>Location:</label>
                      <NarrowDropdown2
                        items={LOC_OPTIONS}
                        value={location}
                        onChange={setLocation}
                        placeholder="Select location"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <p>
                    {modalType === "blockDay"
                      ? `Block the entire day of ${moment(modalStart).format("MMMM D, YYYY")}?`
                      : `Block time from ${moment(modalStart).format("h:mm A")} to ${moment(modalEnd).format("h:mm A")} on ${moment(modalStart).format("MMMM D, YYYY")}?`}
                  </p>

                  <div className="time-inputs">
                    <div className="time-field">
                      <label>Start Time</label>
                      <input
                        type="datetime-local"
                        value={moment(modalStart).format("YYYY-MM-DDTHH:mm")}
                        onChange={(e) =>
                          setModalStart(new Date(e.target.value))
                        }
                      />
                    </div>

                    <div className="time-field">
                      <label>End Time</label>
                      <input
                        type="datetime-local"
                        value={moment(modalEnd).format("YYYY-MM-DDTHH:mm")}
                        onChange={(e) => setModalEnd(new Date(e.target.value))}
                      />
                    </div>

                    <div className="timezone">CT</div>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                {selectedEvent && modalType === "blocker" && (
                  <button className="delete-btn" onClick={handleDelete}>
                    Delete Block
                  </button>
                )}
                <button className="save-btn" onClick={handleModalSubmit}>
                  {modalType === "availability"
                    ? "Save Availability"
                    : "Save Block"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tooltip */}
      {tooltip.visible && (
        <div
          className="tooltip"
          style={{
            position: "fixed",
            top: tooltip.y,
            left: tooltip.x,
            backgroundColor: "#333",
            color: "#fff",
            padding: "8px 12px",
            borderRadius: "4px",
            fontSize: "12px",
            zIndex: 1001,
            pointerEvents: "none",
          }}
        >
          {tooltip.content}
        </div>
      )}

      {/* Appointment Hover Card */}
      {hoverCard.visible && hoverCard.event?.eventType === "appointment" && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            pointerEvents: "none",
          }}
        >
          <div
            style={{ pointerEvents: "auto" }}
            onMouseEnter={cancelHideHoverCard}
            onMouseLeave={scheduleHideHoverCard}
          >
            <AppointmentCard
              appointment={hoverCard.event}
              position={hoverCard.position}
              onClose={() =>
                setHoverCard({
                  visible: false,
                  position: { x: 0, y: 0 },
                  event: null,
                })
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
